import app from '@/main'
import axios from 'axios'
import store from '@/core/store'
import headers from './app-http-headers'
import authApiService from '@/modules/auth/services/auth.api'

let instance = axios.create({
  baseURL: process.env.VUE_APP_BASE_API_URL,
  timeout: 120000,
})

instance.interceptors.request.use(config => {
  config.headers = headers()
  if (!config.skipProgressBar) {
    app.$Progress.start()
  }
  return config
})

instance.interceptors.response.use(response => {
  if (!response.config.skipProgressBar) {
    app.$Progress.finish()
  }

  if(store.getters['auth/user']) {
    //refresh the user's auth token
    store.dispatch('auth/refresh');
  }

  return response
}, error => {
  if (!error.config?.skipProgressBar) {
    app.$Progress.finish()
  }

  if (error && error.response) {
    // Only logout on 400 if it's an actual API key/auth error, not business logic errors
    // Business errors (payment errors, validation errors) should NOT trigger logout
    // Note: API key errors typically return 401, but we check 400 for edge cases
    if (error.response.status === 400) {
      // Check if this is an API key/auth error vs. a business logic error
      const errorData = error.response.data || {}
      const errorCode = errorData.error_code || errorData.code || ''
      
      // Only logout on specific API key/auth error codes, not substring matching
      // Substring matching is too broad and can match legitimate business errors
      // (e.g., "authentication" in 3DS payment flows)
      const isApiKeyError = errorCode === 'api_key_error' ||
                           errorCode === 'authentication_error' ||
                           errorCode === 'invalid_api_key'
      
      // Payment and validation errors should NOT trigger logout
      // Check for business error indicators first
      const isBusinessError = errorCode === 'payment_account_required' ||
                             errorCode === 'card_declined' ||
                             errorCode === 'account_not_ready' ||
                             errorCode === 'account_restricted' ||
                             errorCode === 'account_not_accessible' ||
                             errorCode === 'account_validation_failed' ||
                             errorData.errors || // Validation errors object
                             errorData.error_title || // Payment error titles
                             errorData.error?.error_code // Nested error code structure
      
      // Only logout if it's a confirmed API key error and not a business error
      if (isApiKeyError && !isBusinessError) {
        app.$store.dispatch('auth/autoLogout')
      }
    } else if (error.response.status === 401) { // Auth Error
      if (app.$store.getters['auth/userIsLoggedIn']) {
        app.$store.dispatch('auth/logout')
        .then(() => {
          if (app.$route.name != 'login') {
            app.$router.push({ name: 'login' })
          }
        })
      }
    } else if (error.response.status === 403 && (error.response.data.message === 'This provider has been suspended. Please contact your Administrator!' || error.response.data.message === 'You got suspended from the given provider')) { //Suspended Error)
      authApiService.user()
      .then(response => {
        if (response.data.nurseries.data.find( nursery => !nursery.is_suspended)) {
          localStorage.removeItem('activeBranch')
          app.$store.commit('auth/SET_USER', response.data)
          app.$store.commit('auth/SET_USER_HAS_SUSPENDED_NURSERY', response.data.nurseries.data.find( nursery => nursery.is_suspended || nursery.staffProfile.suspended_at)? true : false)
          app.$router.push({ name: 'choose-branch' })
        } else {
          app.$store.dispatch('auth/logout')
          .then(() => {
            if (app.$route.name != 'login') {
              app.$router.push({ name: 'login' })
            }
          })
        }
      })
    } else if (error.response.status === 403 && error.response.data.message === 'The Service has been scheduled for deletion, please get in touch with an administration member') {
      app.$store.dispatch('auth/logout')
      .then(() => {
        if (app.$route.name != 'login') {
          app.$router.push({ name: 'login' })
        }
      })
    } else if (error.response.status === 403 && error.response.data.message === '') {
      app.$router.push({path: '/'}).catch(() => {})
    }
  }

  return Promise.reject(error)
})

export default instance
